import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import {
  NavigateToBatchResults,
  NavigateToForbidden,
  NavigateToGlobalResultsReports,
  NavigateToGlobalResultsSearch,
  NavigateToGlobalResultsSearchEdit,
  NavigateToGlobalResultsSearchResults,
  NavigateToLogin,
  NavigateToManifestCreatePage,
  NavigateToManifests,
  NavigateToOrders,
  NavigateToPatientChartPage,
  NavigateToPatientEditPage,
  NavigateToPatientEditPageFromOrder,
  NavigateToPatientOrderCreatePage,
  NavigateToPatientOrderEditPage,
  NavigateToPatients,
  NavigateToPreaccessionManifestEdit,
  NavigateToPreaccessionManifestsList,
  NavigateToPreaccessionManifestsSpecimens,
  NavigateToPscSearch,
  NavigateToSupplyOrders
} from './navigations.actions';
import { NavigationExtras, Params } from '@angular/router';
import { AppRouteEnum } from 'src/app/enums/app-route.enum';

interface NavigationStateModel {
  url?: string;
}

@Injectable()
@State<NavigationStateModel>({
  name: 'Navigation'
})
export class NavigationState {
  private readonly store = inject(Store);

  @Action(NavigateToLogin)
  public navigateToLogin(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Login]));
  }

  @Action(NavigateToOrders)
  public navigateToOrders(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Orders]));
  }

  @Action(NavigateToPatients)
  public navigateToPatients(ctx: StateContext<NavigationStateModel>, { extras }: NavigateToPatients): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Patients], undefined, extras));
  }

  @Action(NavigateToPatientChartPage)
  public navigateToPatientChart(ctx: StateContext<NavigationStateModel>, { id }: NavigateToPatientChartPage): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Patients, 'details', id]));
  }

  @Action(NavigateToPatientOrderCreatePage)
  public navigateToPatientOrderCreate(ctx: StateContext<NavigationStateModel>, { id }: NavigateToPatientOrderCreatePage): void {
    this.store.dispatch(
      new Navigate([AppRouteEnum.Orders, 'create'], {
        id
      })
    );
  }

  @Action(NavigateToPatientOrderEditPage)
  public navigateToPatientOrderEditPage(ctx: StateContext<NavigationStateModel>, { id, orderId }: NavigateToPatientOrderEditPage): void {
    this.store.dispatch(
      new Navigate([AppRouteEnum.Orders, orderId], {
        id
      })
    );
  }

  @Action(NavigateToPatientEditPage)
  public navigateToPatientEdit(ctx: StateContext<NavigationStateModel>, { id }: NavigateToPatientEditPage): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Patients, id, 'edit']));
  }

  @Action(NavigateToPatientEditPageFromOrder)
  public navigateToPatientEditPageWithOrderId(
    ctx: StateContext<NavigationStateModel>,
    { id, orderId }: NavigateToPatientEditPageFromOrder
  ): void {
    const queryParams: Params = {
      orderId
    };
    this.store.dispatch(new Navigate([AppRouteEnum.Patients, id, 'edit'], queryParams));
  }

  @Action(NavigateToManifests)
  public navigateToManifests(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Manifests]));
  }

  @Action(NavigateToManifestCreatePage)
  public navigateToManifestCreatePage(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Manifests, 'create']));
  }

  @Action(NavigateToSupplyOrders)
  public navigateToSupplyOrders(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.SupplyOrders]));
  }

  @Action(NavigateToPscSearch)
  public navigateToPscSearch(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.PscSearch]));
  }

  @Action(NavigateToForbidden)
  public navigateToForbidden(ctx: StateContext<NavigationStateModel>, extras?: NavigationExtras | undefined): void {
    this.store.dispatch(new Navigate([AppRouteEnum.Forbidden], undefined, extras));
  }

  @Action(NavigateToGlobalResultsSearch)
  public NavigateToGlobalResultsSearch(ctx: StateContext<NavigationStateModel>, { searchId }: NavigateToGlobalResultsSearch): void {
    this.store.dispatch(new Navigate([AppRouteEnum.GlobalResultsSearch], searchId ? { searchId } : undefined));
  }

  @Action(NavigateToGlobalResultsSearchResults)
  public NavigateToGlobalResultsSearchResults(
    ctx: StateContext<NavigationStateModel>,
    { reportId }: NavigateToGlobalResultsSearchResults
  ): void {
    this.store.dispatch(
      new Navigate([AppRouteEnum.GlobalResultsSearch, AppRouteEnum.GlobalResultsSearchViewResults], reportId ? { reportId } : undefined)
    );
  }

  @Action(NavigateToGlobalResultsSearchEdit)
  public navigateToGlobalResultsSearchEdit(
    ctx: StateContext<NavigationStateModel>,
    { reportId, searchId }: NavigateToGlobalResultsSearchEdit
  ): void {
    const queryParams: Params = { reportId };
    if (searchId) {
      queryParams['searchId'] = searchId;
    }
    this.store.dispatch(new Navigate([AppRouteEnum.GlobalResultsSearch], queryParams));
  }

  @Action(NavigateToBatchResults)
  public navigateToBatchResults(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.BatchResults]));
  }

  @Action(NavigateToGlobalResultsReports)
  public navigateToGlobalResultsReports(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.GlobalResultsReports]));
  }

  @Action(NavigateToPreaccessionManifestsSpecimens)
  public navigateToPreaccessionManifestsSpecimens(
    ctx: StateContext<NavigationStateModel>,
    { manifestId, queryParams }: NavigateToPreaccessionManifestsSpecimens
  ): void {
    const params: Params = { ...(queryParams ?? {}) };

    this.store.dispatch(new Navigate([AppRouteEnum.PreaccessionManifests, manifestId, 'specimens'], params));
  }

  @Action(NavigateToPreaccessionManifestEdit)
  public navigateToPreaccessionManifestEdit(
    ctx: StateContext<NavigationStateModel>,
    { manifestId, queryParams }: NavigateToPreaccessionManifestEdit
  ): void {
    const params: Params = { ...(queryParams ?? {}) };

    this.store.dispatch(new Navigate([AppRouteEnum.PreaccessionManifests, manifestId], params));
  }

  @Action(NavigateToPreaccessionManifestsList)
  public navigateToPreaccessionManifestsList(): void {
    this.store.dispatch(new Navigate([AppRouteEnum.PreaccessionManifests]));
  }
}
